import React, { Fragment, useState, useEffect, useCallback } from 'react';
import MediaQuery from 'react-responsive';
import styled from 'styled-components';

import { Card } from 'antd';

import 'antd/lib/checkbox/style/index.css';

import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import CtaCard from 'components/ui/CtaCard';
import Spacer from 'components/ui/Spacer';

import useDebounce from './hooks/useDebounce';
import Filters from './Filters';
import Selector from './Selector';
import FiltersMobile from './FiltersMobile';
import SelectorMobile from './SelectorMobile';

import { colors } from 'helpers/ColorHelper';
import { titleize } from 'helpers/Formatter';
import { getTranslatedString } from 'helpers/i18n';

const StyledCard = styled(Card)`
  max-height: 500px;
  height: 500px;
`;

const StyledRowBorderBottom = styled(Row)`
  border-bottom: 2px solid ${colors.LIGHT_GREY};
`;

const StyledCenterCol = styled(Col)`
  text-align: center;
  padding: 10px 24px;
`;

const StyledCenterColBorderRight = styled(Col)`
  border-right: 1px solid ${colors.LIGHT_GREY};
  text-align: center;
  padding: 10px 24px;
`;

const StyledColBorderRight = styled(Col)`
  border-right: 1px solid ${colors.LIGHT_GREY};
  padding: 10px 24px;
  overflow-y: scroll;
  height: 450px;
  max-height: 450px;
`;

const StyledSelectCol = styled(Col)`
  padding: 10px 5px;
  overflow-y: scroll;
  height: 450px;
  max-height: 450px;
`;

export const StyledLink = styled(CtaCard)`
  flex-grow: 1;
  padding: 0;
  color: ${colors.PURE_BLUE};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 14px;
  text-align: right;
  float: right;
`;

const StyledDescriptionContainer = styled.div`
  margin-bottom: 20px;
`;

const StyledTitle = styled.span`
  font-size: 18px;
  font-family: 'Circular-Bold';
`;

const StyledStepText = styled.p`
  margin: 0;
`;

const DEBOUNCING_DELAY_IN_MS = 500;

const FilterAndSelect = ({
  filterOptions,
  handleFilter,
  options,
  selected,
  handleSelect,
  verbiage,
  ...rest
}) => {
  const plainOptions = options.map((option) => option.attributes.value);

  const [indeterminate, setIndeterminate] = useState(
    !!selected.length && selected.length < plainOptions.length
  );
  const [checkAll, setCheckAll] = useState(selected.length === plainOptions.length);
  const [filterSelected, setFilterSelected] = useState(Array(filterOptions.length).fill([]));
  const [filtersEmpty, setFiltersEmpty] = useState(true);
  const [query, setQuery] = useState('');

  const debouncedSearchTerm = useDebounce(query, DEBOUNCING_DELAY_IN_MS);

  const _handleFilter = useCallback(async () => {
    let params = {
      query: debouncedSearchTerm,
    };

    filterSelected.forEach((filterCategory) => {
      if (filterCategory.length > 0) {
        filterCategory.forEach((filter) => {
          const filterParam = filter.split('_');
          if (params[filterParam[0]]) {
            params[filterParam[0]] = [...params[filterParam[0]], filterParam[1]];
          } else {
            params[filterParam[0]] = [filterParam[1]];
          }
        });
      }
    });

    await handleFilter(params);
  }, [filterSelected, debouncedSearchTerm, handleFilter]);

  useEffect(() => {
    _handleFilter();
  }, [_handleFilter]);

  const onOptionCheckAllChange = (e) => {
    // Select all that are currently filtered to, or remove all that are in filter view
    handleSelect(
      e.target.checked ? plainOptions : selected.filter((option) => !plainOptions.includes(option))
    );
    setIndeterminate(false);
    setCheckAll(e.target.checked);
  };

  const onOptionSelect = (optionsSelected, clearAll = false) => {
    if (optionsSelected.length === 0 && clearAll) {
      // Clear everything
      handleSelect([]);
      setIndeterminate(false);
      setCheckAll(false);
      return;
    }
    let isIndeterminate = false;
    let isCheckAll = false;

    if (optionsSelected.length > 0) {
      isIndeterminate = !!optionsSelected.length && optionsSelected.length < plainOptions.length;
      isCheckAll = optionsSelected.length === plainOptions.length;
    }

    // Find array currently selected that is not within view ad then append the newly selected (or not selected) to this.
    let currentArray = selected.filter((option) => !plainOptions.includes(option));

    handleSelect(currentArray.concat(optionsSelected));
    setIndeterminate(isIndeterminate);
    setCheckAll(isCheckAll);
  };

  const onCheckFilter = (optionsSelected, index = null) => {
    if (optionsSelected.length === 0 && index === null) {
      setFilterSelected(Array(filterOptions.length).fill([]));
      setFiltersEmpty(true);
    } else {
      let currentArray = [...filterSelected];
      currentArray[index] = optionsSelected;
      setFilterSelected(currentArray);
      setFiltersEmpty(false);
    }
  };

  const handleSearch = (e) => {
    setQuery(e.target.value);
  };

  let totalFiltersSelected = filterSelected.reduce((count, row) => count + row.length, 0);

  return (
    <Fragment>
      <StyledDescriptionContainer>
        <StyledTitle>
          {getTranslatedString('landlordPortal.filterAndSelect.whichOptionsSelected', {
            optionTitle: verbiage.optionTitle,
          })}
        </StyledTitle>
        <StyledStepText>
          {getTranslatedString('landlordPortal.filterAndSelect.step1', {
            optionTitle: verbiage.optionTitle,
          })}
        </StyledStepText>
        <StyledStepText>
          {getTranslatedString('landlordPortal.filterAndSelect.step2', {
            optionTitle: verbiage.optionTitle,
          })}
        </StyledStepText>
      </StyledDescriptionContainer>
      <MediaQuery query="(min-device-width: 751px)">
        <StyledCard bordered={false} bodyStyle={{ padding: 0 }}>
          <StyledRowBorderBottom>
            <StyledCenterColBorderRight span={12}>
              {getTranslatedString('landlordPortal.filterAndSelect.titles.filters')}{' '}
              {`(${totalFiltersSelected})`}
            </StyledCenterColBorderRight>
            <StyledCenterCol span={12}>
              {titleize(verbiage.optionTitle)} {`(${selected.length})`}
            </StyledCenterCol>
          </StyledRowBorderBottom>
          <Row>
            <StyledColBorderRight span={12}>
              <Filters
                filterOptions={filterOptions}
                onCheckFilter={onCheckFilter}
                filterSelected={filterSelected}
                filtersEmpty={filtersEmpty}
              />
            </StyledColBorderRight>
            <StyledSelectCol span={12}>
              <Selector
                handleSearch={handleSearch}
                indeterminate={indeterminate}
                onOptionSelect={onOptionSelect}
                onOptionCheckAllChange={onOptionCheckAllChange}
                checkAll={checkAll}
                options={options}
                selected={selected}
                verbiage={verbiage}
              />
            </StyledSelectCol>
          </Row>
        </StyledCard>
      </MediaQuery>
      <MediaQuery query="(max-device-width: 750px)">
        <FiltersMobile
          filterOptions={filterOptions}
          onCheckFilter={onCheckFilter}
          filterSelected={filterSelected}
          filtersEmpty={filtersEmpty}
        />
        <Spacer mt={2} />
        <SelectorMobile
          handleSearch={handleSearch}
          indeterminate={indeterminate}
          onOptionSelect={onOptionSelect}
          onOptionCheckAllChange={onOptionCheckAllChange}
          checkAll={checkAll}
          options={options}
          selected={selected}
          verbiage={verbiage}
        />
      </MediaQuery>
    </Fragment>
  );
};

export default FilterAndSelect;
